import { useVirtualizer } from '@tanstack/react-virtual'
import { GroupedRow } from 'modules/pond/api/types'
import { useGetShrimpHealthThreshold } from 'modules/notifications/api/queries'
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import palette from 'theme/palette'

import { useDiseaseLabTableSelector } from '../../api/selector'
import { EmptyState } from './components/EmptyState'
import { SkeletonTable } from './components/SkeletonTable'
import { TableHeader } from './components/TableHeader'
import { VirtualRow } from './components/VirtualRow'
import { useCellEditor } from './hooks/useCellEditor'
import { useProcessedRows } from './hooks/useProcessedRows'
import { tableStyles } from './styles'
import { buildColorThresholds } from './utils'

const ROW_HEIGHT = 40

type DiseaseDashboardProps = {
  setLoadingMobile?: (isLoading: boolean) => void
}

export default function DiseaseDashboard({ setLoadingMobile }: DiseaseDashboardProps) {
  const { t } = useTranslation('pondOverview')

  const { data, isLoading, hasNextPage, fetchNextPage } =
    useDiseaseLabTableSelector()

  const scrollRef = useRef<HTMLDivElement>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isPulling, setIsPulling] = useState(false)

  const isMobile = new URLSearchParams(window.location.search).get('isMobile') === 'true'
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor
    const ios =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    setIsIOS(ios)
  }, [])

  const { editedCells, handleCellChange } = useCellEditor()

  const { flatRows: processedRows, disabledColumns: finalDisabledColumns } = useProcessedRows(
    data?.rows as GroupedRow[],
    editedCells
  )

  // NK-273: cells are coloured by the shrimp-health alert settings.
  const { data: shrimpHealthThresholds } = useGetShrimpHealthThreshold()
  const colorThresholds = useMemo(
    () => buildColorThresholds(processedRows, shrimpHealthThresholds),
    [processedRows, shrimpHealthThresholds]
  )

  // The mobile webview scales this table via a `transform: scale()` wrapper
  // (ZoomableTableWrapper), NOT CSS `zoom`. `transform` scales the whole painted
  // subtree — sticky header/columns included — and, crucially, leaves the inner
  // scroller (scrollRef) operating in natural, un-scaled coordinates. So
  // @tanstack/react-virtual reads `scrollTop` and measures the container in one
  // consistent coordinate space on WebKit (no zoom-vs-layout desync), and the
  // virtual rows' `translateY` offsets line up exactly with their real
  // positions. This is the same proven setup as the sibling "ข้อมูลบ่อ"
  // (TableSummaryDashboard) table, so virtualization stays enabled on iOS with
  // no row/sticky-column overlap — the desync that earlier forced a
  // de-virtualization special-case only existed under CSS `zoom`, which is gone.
  const rowVirtualizer = useVirtualizer({
    count: processedRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: isMobile ? 4 : 10,
  })

  // NK-305: the spinner and the double-click guard use only this component's own
  // isLoadingMore. React Query's isFetchingNextPage is computed optimistically on
  // the first render after a remount, from the fetchMeta the previous
  // fetchNextPage left behind, so after "load more" → another tab → back it read
  // true; when the refetch then came back unchanged nothing re-rendered, and the
  // button kept spinning (and stayed disabled).
  const handleLoadMore = useCallback(async () => {
    if (!hasNextPage || isLoadingMore) return

    setIsLoadingMore(true)
    try {
      await fetchNextPage()
    } finally {
      setIsLoadingMore(false)
    }
  }, [hasNextPage, isLoadingMore, fetchNextPage])

  useEffect(() => {
    if (!isMobile || !scrollRef.current) return

    const scrollElement = scrollRef.current
    let startX = 0
    let startY = 0
    let isHorizontalMove = false

    const handleTouchStart = (e: TouchEvent) => {
      if (scrollElement.scrollLeft === 0) {
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
        isHorizontalMove = false
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!startX || !startY) return

      const currentX = e.touches[0].clientX
      const currentY = e.touches[0].clientY
      const diffX = currentX - startX
      const diffY = currentY - startY

      if (Math.abs(diffX) > Math.abs(diffY)) {
        isHorizontalMove = true
      }

      if (isHorizontalMove && diffX > 50 && scrollElement.scrollLeft === 0) {
        setIsPulling(true)
      }
    }

    const handleTouchEnd = async () => {
      if (isPulling && hasNextPage) {
        await handleLoadMore()
      }
      startX = 0
      startY = 0
      isHorizontalMove = false
      setIsPulling(false)
    }

    scrollElement.addEventListener('touchstart', handleTouchStart)
    scrollElement.addEventListener('touchmove', handleTouchMove)
    scrollElement.addEventListener('touchend', handleTouchEnd)

    return () => {
      scrollElement.removeEventListener('touchstart', handleTouchStart)
      scrollElement.removeEventListener('touchmove', handleTouchMove)
      scrollElement.removeEventListener('touchend', handleTouchEnd)
    }
  }, [hasNextPage, handleLoadMore, isPulling, isMobile])

  // NK-305: cells save on blur, and leaving the page does not always blur them —
  // switching to another app or tab left the typed value unsent. Blur the cell
  // being edited when the page is hidden or closed so it saves.
  useEffect(() => {
    const flushFocusedCell = () => {
      const active = document.activeElement
      if (active instanceof HTMLElement && scrollRef.current?.contains(active)) {
        active.blur()
      }
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushFocusedCell()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', flushFocusedCell)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', flushFocusedCell)
    }
  }, [])

  useEffect(() => {
    if (isMobile) {
      if (setLoadingMobile) {
        setLoadingMobile(isLoading || isLoadingMore)
      }
    }
  }, [isMobile, isIOS, isLoading, isLoadingMore, setLoadingMobile])

  useEffect(() => {
    if (!isMobile || !isIOS || !scrollRef.current) return

    const scrollElement = scrollRef.current

    const handleScroll = () => {
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth
      const maxScrollTop = scrollElement.scrollHeight - scrollElement.clientHeight

      if (scrollElement.scrollLeft < 0) {
        scrollElement.scrollLeft = 0
      } else if (scrollElement.scrollLeft > maxScrollLeft) {
        scrollElement.scrollLeft = maxScrollLeft
      }

      if (scrollElement.scrollTop < 0) {
        scrollElement.scrollTop = 0
      } else if (scrollElement.scrollTop > maxScrollTop) {
        scrollElement.scrollTop = maxScrollTop
      }
    }

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [isMobile, isIOS])

  if (isLoading) return <SkeletonTable />

  if (!data?.rows || data.rows.length === 0) return <EmptyState />

  return (
    <div style={{ position: 'relative', height: isMobile ? '100%' : undefined }}>
      {/* On the mobile webview the table is scaled by a transform wrapper, which
          does not affect layout. The bounded height above makes the inner
          scroller (scrollRef, also the virtualizer's scroll element) a real
          viewport inside the scaled subtree — otherwise scrolling collapses and
          bottom rows get clipped. Mirrors TableSummaryDashboard. */}
      <div ref={scrollRef} style={tableStyles.container(isMobile, isIOS)}>
        <div style={tableStyles.tableWrapper}>
          <TableHeader
            columns={data.columns}
            hasNextPage={!isMobile && hasNextPage}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
          />

          {isMobile && isPulling && (
            <div
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1000,
                color: palette.light.primary.main,
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {t('lab.table.releaseToLoad')}
            </div>
          )}

          <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = processedRows[virtualRow.index]
              const disabledColsForGroup = finalDisabledColumns.get(row.group) || new Set<number>()

              return (
                <VirtualRow
                  key={virtualRow.index}
                  row={row}
                  rowHeight={virtualRow.size}
                  translateY={virtualRow.start}
                  disabledColumns={disabledColsForGroup}
                  colorThresholds={colorThresholds[virtualRow.index]}
                  onCellChange={handleCellChange}
                  editedCells={editedCells}
                  isMobile={isMobile}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
